(function (root, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  root.CartDrawerCampaigns = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  function getThresholdForMarket(marketHandle, nzThreshold, auThreshold) {
    return marketHandle === 'australia' ? auThreshold : nzThreshold;
  }

  function calculateProgress(total, threshold) {
    if (!threshold || threshold <= 0) {
      return { remaining: 0, percentage: 0, qualified: false };
    }

    const remaining = Math.max(threshold - total, 0);
    const percentage = Math.min((total / threshold) * 100, 100);

    return {
      remaining,
      percentage,
      qualified: total >= threshold,
    };
  }

  function shouldShowRecommendationControls(track) {
    return track.scrollWidth > track.clientWidth + 1;
  }

  function getDrawer() {
    return document.querySelector('cart-drawer');
  }

  function closeActiveDrawer(drawer) {
    if (!drawer || !drawer.classList.contains('active')) return false;

    drawer.close();
    return true;
  }

	function bindCartDrawerQuickViewClose() {
		if (document.documentElement.dataset.cartDrawerQuickViewCloseBound) return;

		document.documentElement.dataset.cartDrawerQuickViewCloseBound = 'true';
		document.addEventListener(
			'quick-add:ready',
			(event) => {
				if (!event.target.id.startsWith('CartDrawerQuickView-')) return;
				closeActiveDrawer(getDrawer());
			},
		);
	}

  async function refreshDrawer() {
    const url = new URL(window.routes.cart_url, window.location.origin);
    url.searchParams.set('section_id', 'cart-drawer');

    const previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);

    const response = await fetch(url.toString());
    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const drawerMarkup = parsed.querySelector('#CartDrawer');
    const drawer = getDrawer();

    if (!drawerMarkup || !drawer) {
      throw new Error('Unable to refresh the cart drawer.');
    }

    drawer.renderContents({ sections: { 'cart-drawer': html } });
  }

  async function addRecommendedProduct(button) {
    const variantId = button.dataset.cartAddVariantId;

    if (!variantId) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(window.routes.cart_add_url, {
        ...fetchConfig(),
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });

      if (!response.ok) throw new Error('Unable to add this product.');

      await refreshDrawer();
    } catch (error) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.dataset.cartAddError = 'true';
      button.textContent = 'Try again';
    }
  }

  function bindRecommendedProducts(scope) {
    scope.querySelectorAll('[data-cart-add-variant-id]').forEach((button) => {
      if (button.dataset.cartCampaignBound) return;

      button.dataset.cartCampaignBound = 'true';
      button.addEventListener('click', () => addRecommendedProduct(button));
    });
  }

  function bindMouseDrag(track) {
    const desktopMedia = window.matchMedia('(min-width: 750px)');
    const dragThreshold = 5;
    let pointerId = null;
    let startX = 0;
    let startScrollLeft = 0;
    let didDrag = false;
    let suppressClick = false;

    const finishDrag = (event) => {
      if (pointerId === null) return;

      const activePointerId = pointerId;
      suppressClick = didDrag && event?.type === 'pointerup';
      pointerId = null;
      didDrag = false;
      track.classList.remove('is-mouse-dragging');

      if (track.hasPointerCapture?.(activePointerId)) {
        track.releasePointerCapture(activePointerId);
      }
    };

    track.addEventListener('pointerdown', (event) => {
      if (!desktopMedia.matches || event.pointerType !== 'mouse' || event.button !== 0) return;
      if (!shouldShowRecommendationControls(track)) return;

      pointerId = event.pointerId;
      startX = event.clientX;
      startScrollLeft = track.scrollLeft;
      didDrag = false;
      suppressClick = false;
    });

    track.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;

      const distance = event.clientX - startX;
      if (!didDrag) {
        if (Math.abs(distance) < dragThreshold) return;

        didDrag = true;
        track.setPointerCapture?.(pointerId);
      }

      track.classList.add('is-mouse-dragging');
      track.scrollLeft = startScrollLeft - distance;
      event.preventDefault();
    });

    track.addEventListener('pointerup', finishDrag);
    track.addEventListener('pointercancel', finishDrag);
    track.addEventListener('lostpointercapture', finishDrag);
    track.addEventListener('dragstart', (event) => event.preventDefault());
    track.addEventListener(
      'click',
      (event) => {
        if (!suppressClick) return;

        event.preventDefault();
        event.stopPropagation();
        suppressClick = false;
      },
      true
    );
  }

  function bindRecommendationCarousel(scope) {
    const track = scope.querySelector('.cart-drawer__recommendations-track');
    const controls = scope.querySelector('[data-cart-recommendations-controls]');

    if (!track || !controls) return;

    if (track.dataset.cartRecommendationsBound) {
      track.cartRecommendationsUpdateControls?.();
      return;
    }

    track.dataset.cartRecommendationsBound = 'true';
    bindMouseDrag(track);

    const updateControls = () => {
      const isScrollable = shouldShowRecommendationControls(track);
      controls.hidden = !isScrollable;
      track.classList.toggle('is-mouse-draggable', isScrollable);
    };

    const scheduleControlsUpdate = () => {
      requestAnimationFrame(() => requestAnimationFrame(updateControls));
    };

    track.cartRecommendationsUpdateControls = scheduleControlsUpdate;
    updateControls();
    scheduleControlsUpdate();

    if (typeof ResizeObserver === 'function') {
      const observer = new ResizeObserver(scheduleControlsUpdate);
      observer.observe(track);
      track.cartRecommendationsResizeObserver = observer;
    } else if (typeof window !== 'undefined') {
      window.addEventListener('resize', scheduleControlsUpdate);
    }

    track.querySelectorAll('img').forEach((image) => {
      if (!image.complete) image.addEventListener('load', scheduleControlsUpdate, { once: true });
    });

    scope
      .querySelectorAll('[data-cart-recommendations-previous], [data-cart-recommendations-next]')
      .forEach((button) => {
        button.addEventListener('click', () => {
          const card = track.querySelector('.cart-drawer__recommendation');
          const cardWidth = card ? card.getBoundingClientRect().width : 0;
          const direction = button.hasAttribute('data-cart-recommendations-next') ? 1 : -1;

          track.scrollBy({ left: direction * cardWidth, behavior: 'smooth' });
        });
      });
  }

  function init() {
    bindRecommendedProducts(document);
    bindRecommendationCarousel(document);
    bindCartDrawerQuickViewClose();
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('DOMContentLoaded', init);
    document.addEventListener('shopify:section:load', (event) => {
      bindRecommendedProducts(event.target);
      bindRecommendationCarousel(event.target);
    });
  }

  return {
    calculateProgress,
    closeActiveDrawer,
    getThresholdForMarket,
    shouldShowRecommendationControls,
    bindRecommendedProducts,
    bindRecommendationCarousel,
  };
});
